import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert } from 'lucide-react';
import { DEVELOPMENT_DOMAINS } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  Field,
  FieldGroup,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { DictationButton } from '@/components/DictationButton';
import { ApiError, apiData } from '@/lib/api';
import { todayISO } from '@/lib/format';
import type { ChildSummary, LanguageWarningRef } from '@/types';

/**
 * 관찰기록 작성 — 명세서 §5.14 (교사 전용, 학부모 비공개)
 *
 * 자유 서술 + 발달영역 태그 + 원아 태그. 발달기록(§6)의 근거로 인용되므로
 * 아이를 최소 한 명 태그해야 저장된다. 언어 경고(§6.9)는 저장을 막지 않는다 —
 * 관찰 원문은 있는 그대로가 근거다.
 */
export function ObservationEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [observedOn, setObservedOn] = useState(todayISO());
  const [content, setContent] = useState('');
  const [childIds, setChildIds] = useState<string[]>([]);
  const [domainCodes, setDomainCodes] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<LanguageWarningRef[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const save = useMutation({
    mutationFn: () =>
      apiData<{ warnings: LanguageWarningRef[] }>('/observations', {
        method: 'POST',
        body: { observedOn, content: content.trim(), childIds, domainCodes },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['observations'] });
      if (result.warnings.length === 0) {
        navigate('/observations', { replace: true });
        return;
      }
      // 경고가 있으면 바로 떠나지 않고 보여 준다 — 다음 기록에서 표현을 고르는 데 도움이 된다.
      setWarnings(result.warnings);
      setSaved(true);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  if (children.isPending) return <Spinner />;

  return (
    <Screen withNav={false}>
      <AppBar title="관찰 남기기" back />

      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (!save.isPending && !saved) save.mutate();
        }}
        className="flex flex-col gap-4 p-4"
      >
        {/* 놀이 지도 중엔 자판보다 말이 빠르다 — 화면을 열자마자 말할 수 있게 맨 위에 둔다 (v1.19) */}
        <DictationButton
          onText={(text) => setContent((prev) => (prev ? `${prev.trimEnd()} ${text}` : text))}
        />

        <Card className="flex flex-col gap-4">
          <Field label="관찰한 날" required>
            <Input
              type="date"
              value={observedOn}
              onChange={(e) => setObservedOn(e.target.value)}
              required
            />
          </Field>

          <FieldGroup label="아이" required>
            <div className="flex flex-wrap gap-2">
              {children.data?.map((child) => (
                <Chip
                  key={child.id}
                  tone={childIds.includes(child.id) ? 'primary' : 'neutral'}
                  selected={childIds.includes(child.id)}
                  onClick={() => setChildIds((prev) => toggle(prev, child.id))}
                >
                  {child.name}
                </Chip>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup label="발달영역 (선택)">
            <div className="flex flex-wrap gap-2">
              {DEVELOPMENT_DOMAINS.map((domain) => (
                <Chip
                  key={domain.code}
                  tone={domainCodes.includes(domain.code) ? 'primary' : 'neutral'}
                  selected={domainCodes.includes(domain.code)}
                  onClick={() => setDomainCodes((prev) => toggle(prev, domain.code))}
                >
                  {domain.name}
                </Chip>
              ))}
            </div>
          </FieldGroup>

          <Field label="관찰한 모습" required>
            <Textarea
              rows={6}
              required
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="언제, 어디서, 어떤 행동을 했는지 본 대로 적어 주세요."
            />
          </Field>
        </Card>

        {warnings.length > 0 && (
          <Card className="border border-secondary">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <CircleAlert size={15} /> 저장했어요 — 살펴볼 표현이 있어요
            </p>
            <ul className="flex flex-col gap-2 text-sm">
              {warnings.map((warning, index) => (
                <li key={`${warning.term}-${index}`}>
                  <b>“{warning.term}”</b> — {warning.reason}
                  <br />
                  <span className="text-ink-sub">제안: {warning.suggestions.join(' / ')}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        {saved ? (
          <Button block onClick={() => navigate('/observations', { replace: true })}>
            목록으로
          </Button>
        ) : (
          <Button
            type="submit"
            block
            loading={save.isPending}
            disabled={childIds.length === 0 || !content.trim()}
          >
            저장하기
          </Button>
        )}
      </form>
    </Screen>
  );
}
